import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Mail } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

/**
 * PAYMENT-REVERSAL HOLD (contractor view)
 * ---------------------------------------------------------------------------
 * Rendered only when mkt_profiles.status = 'suspended', which is what
 * mkt_apply_payment_reversal sets when a charge is refunded, disputed,
 * charged back or reversed. In that state mkt_claim_lead_core refuses every
 * unlock with `account_not_approved`, so the contractor MUST be told why —
 * a silently dead Unlock button is the worst possible experience.
 *
 * It states the three facts plainly: credits are frozen, unlocks are paused,
 * and the hold lifts when the payment is resolved. It never invents a reason.
 */
const AccountHoldNotice: React.FC = () => {
  const { lang, path } = useI18n();
  const { profile } = useAuth();
  const es = lang === 'es';
  const [reason, setReason] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [kind, setKind] = useState<string>('');

  const suspended = String((profile as any)?.status || '') === 'suspended';

  useEffect(() => {
    if (!profile?.id || !suspended) return;
    let alive = true;
    supabase
      .from('mkt_refunds')
      .select('reason,amount,status,created_at')
      .eq('user_id', profile.id)
      .in('status', ['refunded', 'disputed'])
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (!alive || !data?.length) return;
        setReason(data[0].reason || null);
        setAmount(typeof data[0].amount === 'number' ? data[0].amount : null);
        setKind(String(data[0].status || ''));
      });
    return () => {
      alive = false;
    };
  }, [profile?.id, suspended]);

  if (!suspended) return null;

  const disputed = kind === 'disputed';

  return (
    <section className="mt-6 rounded-2xl border-2 border-red-300 bg-red-50 p-5">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
        <div>
          <h2 className="text-base font-extrabold text-red-900">
            {es ? 'Tu cuenta está en espera' : 'Your account is on hold'}
          </h2>
          <p className="mt-1 text-sm text-red-800">
            {disputed
              ? es
                ? 'Se abrió una disputa/contracargo sobre un pago de tu cuenta.'
                : 'A dispute or chargeback was opened on a payment for your account.'
              : es
              ? 'Un pago de tu cuenta fue reembolsado.'
              : 'A payment on your account was refunded.'}{' '}
            {es
              ? 'Mientras se resuelve: los créditos restantes están congelados y no se pueden desbloquear proyectos nuevos.'
              : 'While it is being resolved: your remaining credits are frozen and new project unlocks are paused.'}
          </p>

          <ul className="mt-3 space-y-1 text-sm text-red-800">
            <li>
              • {es ? 'Créditos congelados' : 'Credits frozen'}
              {amount !== null && amount > 0 ? ` · $${(amount / 100).toFixed(2)}` : ''}
            </li>
            <li>• {es ? 'Desbloqueos pausados' : 'Lead unlocks paused'}</li>
            <li>
              •{' '}
              {es
                ? 'Los proyectos que ya desbloqueaste siguen en tu panel'
                : 'Projects you already unlocked stay in your dashboard'}
            </li>
          </ul>

          {reason && (
            <p className="mt-3 rounded-lg bg-white/70 px-3 py-2 text-xs text-red-900">
              {es ? 'Motivo registrado: ' : 'Recorded reason: '}
              <span className="font-bold">{reason}</span>
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to={path('/contact')}
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700"
            >
              <Mail className="h-4 w-4" />
              {es ? 'Resolver con soporte' : 'Resolve with support'}
            </Link>
            <Link
              to={path('/pricing')}
              className="inline-flex items-center rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50"
            >
              {es ? 'Ver membresías' : 'View memberships'}
            </Link>
          </div>

          <p className="mt-3 text-xs text-red-700">
            {es
              ? 'Nota: al levantar la espera no se devuelven créditos. Un pago nuevo y verificado es lo que restablece el saldo.'
              : 'Note: lifting the hold does not hand credits back. A new verified payment is what restores a balance.'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default AccountHoldNotice;
